import React, { useEffect, useState } from "react";
import { DatePicker, Input, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";

import { STATUS_OPTIONS } from "../constants/adminLeaves.constants";

import type {
  AdminLeavesFilters as AdminLeavesFiltersType,
  EmployeeOption,
  LeaveStatus,
  LeaveTypeOption,
} from "../../../../../../core/data/redux/admin-leaves/types";

const { RangePicker } = DatePicker;

interface AdminLeavesFiltersProps {
  filters: AdminLeavesFiltersType;
  employees: EmployeeOption[];
  leaveTypes: LeaveTypeOption[];
  onFilterChange: (partial: Partial<AdminLeavesFiltersType>) => void;
  onReset: () => void;
}

const filterOption = (input: string, option?: { label: string; value: any }) =>
  (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase());

const AdminLeavesFilters: React.FC<AdminLeavesFiltersProps> = ({
  filters,
  employees,
  leaveTypes,
  onFilterChange,
  onReset,
}) => {
  const [localSearch, setLocalSearch] = useState(filters.search || "");

  useEffect(() => {
    setLocalSearch(filters.search || "");
  }, [filters.search]);

  const activeFiltersCount =
    (filters.search ? 1 : 0) +
    (filters.employeeId !== null ? 1 : 0) +
    (filters.leaveTypeId !== null ? 1 : 0) +
    (filters.status !== null ? 1 : 0) +
    (filters.startDate || filters.endDate ? 1 : 0);

  const handleSearchCommit = () => {
    onFilterChange({ search: localSearch.trim(), page: 1 });
  };

  const handleRangeChange = (range: [Dayjs | null, Dayjs | null] | null) => {
    const [start, end] = range || [null, null];
    onFilterChange({
      startDate: start ? start.format("YYYY-MM-DD") : null,
      endDate: end ? end.format("YYYY-MM-DD") : null,
      page: 1,
    });
  };

  const rangeValue: [Dayjs | null, Dayjs | null] = [
    filters.startDate ? dayjs(filters.startDate) : null,
    filters.endDate ? dayjs(filters.endDate) : null,
  ];

  return (
    <div className="card">
      <div className="card-header border-0 pb-0">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div>
            <h4 className="mb-1 d-flex align-items-center">
              <i className="ti ti-calendar-event text-info me-2" style={{ fontSize: 20 }} />
              Leave Requests
            </h4>
            <p className="text-muted mb-0">
              Review, approve, or reject employee leave applications
            </p>
          </div>
        </div>
      </div>

      <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2">
        <div className="d-flex gap-2 flex-wrap">
          <div className="d-flex gap-2 mb-2 me-2">
            <Input
              placeholder="Search by employee name..."
              value={localSearch}
              onChange={(e) => {
                const value = e.target.value;
                setLocalSearch(value);
                if (value === "") {
                  onFilterChange({ search: "", page: 1 });
                }
              }}
              onPressEnter={handleSearchCommit}
              style={{ width: 240, minHeight: 38 }}
              allowClear
            />
            <button
              type="button"
              className="btn btn-info"
              onClick={handleSearchCommit}
              style={{ minHeight: 38 }}
            >
              <i className="ti ti-search me-1" />
            </button>
          </div>
        </div>

        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-2">
          <div className="me-2 mb-2">
            <Select
              showSearch
              filterOption={filterOption}
              style={{ width: 200, minHeight: 38 }}
              placeholder="Employee"
              value={filters.employeeId ?? undefined}
              allowClear
              onChange={(value) =>
                onFilterChange({
                  employeeId: value !== undefined && value !== null ? Number(value) : null,
                  page: 1,
                })
              }
              options={employees.map((e) => ({
                value: Number(e.value),
                label: e.label,
              }))}
            />
          </div>
          <div className="me-2 mb-2">
            <Select
              showSearch
              filterOption={filterOption}
              style={{ width: 180, minHeight: 38 }}
              placeholder="Leave Type"
              value={filters.leaveTypeId ?? undefined}
              allowClear
              onChange={(value) =>
                onFilterChange({
                  leaveTypeId: value !== undefined && value !== null ? Number(value) : null,
                  page: 1,
                })
              }
              options={leaveTypes.map((t) => ({
                value: Number(t.value),
                label: t.label,
              }))}
            />
          </div>
          <div className="me-2 mb-2">
            <Select
              style={{ width: 140, minHeight: 38 }}
              placeholder="Status"
              value={filters.status ?? undefined}
              allowClear
              onChange={(value) =>
                onFilterChange({ status: (value as LeaveStatus) ?? null, page: 1 })
              }
              options={STATUS_OPTIONS}
            />
          </div>
          <div className="me-2">
            <RangePicker
              value={rangeValue}
              onChange={handleRangeChange}
              format="DD MMM YYYY"
              style={{ minHeight: 38 }}
              allowClear
            />
          </div>
          <div>
            <button
              type="button"
              className={`btn btn-md ${activeFiltersCount > 0 ? "btn-warning" : "btn-outline-danger"} mb-2 position-relative py-2`}
              onClick={onReset}
              disabled={activeFiltersCount === 0}
            >
              <i className="ti ti-refresh me-1" />
              Reset
              {activeFiltersCount > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: "10px" }}
                >
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLeavesFilters;
